'server only';
import StoryblokClient, { ISbConfig } from 'storyblok-js-client';

import { STORYBLOK_TOKENS, STORYBLOK_SPACE_ID } from '@monoimin/constants';

import { Logger } from '../debug/Logger';
import { LogEntry } from '../debug/logger.types';

import { sleep } from '../helpers/sleep';

type NextFetchRequestConfig = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

const CONFIG = {
  CACHE: {
    REVALIDATE_TIME: 86_400, // 24 hours
    CACHE_TAGS: ['storyblok', 'content'] as string[],
  },
} as const;

const logger = new Logger('Storyblok API', 'server', true);

const getResponseHeaders = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
};

const logResponse = (
  response: Response,
  url: string,
  startTime: number,
  type: 'INFO' | 'ERROR' = 'INFO',
  error?: Error | unknown
) => {
  const headers = getResponseHeaders(response);
  const amzCfId = headers['x-amz-cf-id'] ?? 'unknown x-amz-cf-id';

  const xCache = headers['x-cache'] ?? 'unknown cache';

  const logData: LogEntry = {
    type,
    endpoint: url,
    cacheStatus: xCache,
    time: Date.now() - startTime,
    headers,
    cfId: amzCfId,
  };

  if (error) {
    logData.error = error;
  }

  logger.logApiRequest(logData);
};

const getFinalCacheTime = (isDraft: boolean, cacheTime?: number): number => {
  if (isDraft) {
    return 0;
  } else if (typeof cacheTime === 'number') {
    return cacheTime;
  } else {
    return CONFIG.CACHE.REVALIDATE_TIME;
  }
};

const createCacheOptions = (
  finalCacheTime: number,
  options?: RequestInit
): NextFetchRequestConfig => {
  const baseOptions: NextFetchRequestConfig = {
    ...options,
    next: {
      revalidate: finalCacheTime,
      tags: [...CONFIG.CACHE.CACHE_TAGS],
    },
  };

  // For Next.js 15, ensure we're using the default cache behavior for production builds
  if (finalCacheTime > 0) {
    // Let Next.js handle caching with its optimized behavior
    baseOptions.cache = 'force-cache';
  } else {
    // For draft/development, don't cache
    baseOptions.cache = 'no-store';
  }

  return baseOptions;
};

const handleFetchError = async (
  error: unknown,
  url: RequestInfo | URL,
  urlString: string,
  startTime: number,
  options?: RequestInit
): Promise<Response> => {
  await sleep(1000);

  const fallbackResponse = await fetch(url, options).catch(() => {
    return new Response(JSON.stringify({ error: 'Failed to fetch data' }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' },
    });
  });

  logResponse(fallbackResponse, urlString, startTime, 'ERROR', error);
  return fallbackResponse;
};

export const newStoryblokApi = (
  isDraft: boolean,
  _?: 'server',
  cacheTime?: number
) => {
  const finalCacheTime = getFinalCacheTime(isDraft, cacheTime);

  const params: ISbConfig = {
    accessToken: STORYBLOK_TOKENS.server.preview,
    fetch: async function (url: RequestInfo | URL, options?: RequestInit) {
      const startTime = Date.now();
      let urlString = url.toString();

      // Fix the "cv=undefined" and other undefined values issue by cleaning the URL
      if (urlString.includes('=undefined')) {
        // More comprehensive URL cleaning to handle any undefined values
        urlString = urlString
          .replace(/[&?]\w+=undefined(&|$)/g, '$1') // Remove any parameter=undefined with proper cleanup
          .replace(/\?&/g, '?') // Fix ?& at start of query
          .replace(/&&+/g, '&') // Remove multiple &
          .replace(/[&?]$/, ''); // Remove trailing & or ?
      }

      try {
        const cacheOptions = createCacheOptions(finalCacheTime, options);

        // Use the global fetch directly
        const response = await fetch(urlString, cacheOptions);

        logResponse(response, urlString, startTime);
        return response;
      } catch (error: unknown) {
        return handleFetchError(error, url, urlString, startTime, options);
      }
    },
  };

  const Storyblok = new StoryblokClient(params);

  return {
    Storyblok,
    spaceId: STORYBLOK_SPACE_ID,
    revalidate: finalCacheTime,
  };
};
